import dayjs from 'dayjs'
import { fetchApi } from './api'
import type { EtlJob, EtlJobCatalog, EtlJobParameter, EtlJobTriggerRequest } from '../types/etlJobs'

const BASE = '/api/v1/etl/jobs'

/** Every job this deployment has registered, with any active halt. */
export async function getEtlJobCatalog(): Promise<EtlJobCatalog> {
  return fetchApi<EtlJobCatalog>(BASE)
}

/**
 * One job, for a deep link into its run form.
 *
 * An unregistered id is a 404 whose body is `{ message }`, which fetchApi carries as the ApiError message —
 * so a caller can tell "no such job" (errorKind 'not-found') from a failed request.
 */
export async function getEtlJob(jobId: string): Promise<EtlJob> {
  return fetchApi<EtlJob>(`${BASE}/${encodeURIComponent(jobId)}`)
}

/**
 * Asks for a run.
 *
 * Returns as soon as the request is recorded, which is not the same as the job having started: the API
 * writes a row and Automaton enqueues it moments later. Follow it with {@link getEtlJobTriggerRequest}.
 *
 * BEARER-ONLY. The endpoint is pinned to an Entra sign-in (EtlWriteAuthorization) and refuses the shared
 * API key with a 401 even in Development, so with no Entra token this throws BearerRequiredError without
 * sending anything — it never falls back to the key.
 */
export async function triggerEtlJob(
  jobId: string,
  parameters: Record<string, string>
): Promise<EtlJobTriggerRequest> {
  return fetchApi<EtlJobTriggerRequest>(`${BASE}/${encodeURIComponent(jobId)}/runs`, {
    method: 'POST',
    body: JSON.stringify({ parameters }),
    auth: 'bearer-only',
  })
}

/** What became of one request. Poll until it leaves `Requested`. */
export async function getEtlJobTriggerRequest(requestId: string): Promise<EtlJobTriggerRequest> {
  return fetchApi<EtlJobTriggerRequest>(`${BASE}/runs/${requestId}`)
}

/** The most recent requests, newest first. */
export async function getRecentEtlJobTriggerRequests(limit = 15): Promise<EtlJobTriggerRequest[]> {
  return fetchApi<EtlJobTriggerRequest[]>(`${BASE}/runs?limit=${limit}`)
}

/**
 * Turns a parameter's declared default into a concrete value.
 *
 * The server sends relative tokens (`today`, `today+1day`, `today-1month`) rather than literal dates,
 * because a default baked at publish time would drift every day the worker stayed up. Anything
 * unrecognized is passed through as a literal, so a plain default still works.
 */
export function resolveParameterDefault(parameter: EtlJobParameter): string {
  const token = parameter.default?.trim()
  if (!token) return ''

  const match = /^today(?:([+-])(\d+)(day|days|week|weeks|month|months|year|years))?$/i.exec(token)
  if (!match) return token

  let value = dayjs().startOf('day')

  if (match[1] && match[2] && match[3]) {
    const amount = Number(match[2])
    // dayjs takes the singular; the token may carry either.
    const unit = match[3].toLowerCase().replace(/s$/, '') as 'day' | 'week' | 'month' | 'year'
    value = match[1] === '+' ? value.add(amount, unit) : value.subtract(amount, unit)
  }

  // yyyy-MM-dd, never an instant. The source's last-updated column carries no timezone, so sending one
  // would filter against it hours out -- which the API refuses outright.
  return value.format('YYYY-MM-DD')
}

/** The initial form values for a job, from its declared defaults. */
export function initialParameterValues(parameters: EtlJobParameter[]): Record<string, string> {
  return Object.fromEntries(parameters.map((p) => [p.name, resolveParameterDefault(p)]))
}
